import React, { useState } from 'react';
import { motion } from 'framer-motion';

interface CLEARArchitectureDiagramProps {
  className?: string;
  animated?: boolean;
  onComponentClick?: (component: string) => void;
}

const CLEARArchitectureDiagram: React.FC<CLEARArchitectureDiagramProps> = ({ 
  className = '', 
  animated = true,
  onComponentClick 
}) => {
  
  const spectrumOrange = '#FF4C00';
  const spectrumAccent = '#FF6B35';
  const spectrumDark = '#0D0D0D';
  const spectrumMuted = '#8B8680';
  const spectrumLight = '#ECECE9';
  
  // Enhanced color palette with diverse colors
  const layerColors = {
    core: '#FF4C00',          // Bright orange for core data mesh
    layer1: {
      eventDetection: '#2563EB',    // Blue for Event Detection
      fieldOperations: '#059669',   // Green for Field Operations  
      situationAnalysis: '#DC2626', // Red for Situation Analysis
      cashDistribution: '#7C3AED'   // Purple for Cash Distribution
    },
    layer2: {
      dataSupport: '#60A5FA',       // Light blue for data-related support
      operationalSupport: '#34D399', // Light green for operational support
      analysisSupport: '#F87171',    // Light red for analysis support
      distributionSupport: '#A78BFA' // Light purple for distribution support
    },
    layer3: '#6B7280',        // Cool gray for external integrations
    connections: {
      primary: '#FF4C00',     // Core to Layer 1 - keep orange for emphasis
      secondary: '#4F46E5',   // Layer 1 to Layer 2 - indigo
      tertiary: '#6B7280'     // Layer 2 to Layer 3 - gray
    },
    circles: {
      layer1: '#2563EB',      // Blue circle for AI components
      layer2: '#059669',      // Green circle for support systems  
      layer3: '#6B7280'       // Gray circle for external integrations
    }
  };

  const [hoveredComponent, setHoveredComponent] = useState<string | null>(null);

  // Component definitions with their positions in concentric circles
  const components = [
    // Core - Data Mesh (center)
    {
      id: 'data-mesh',
      name: 'DATA MESH',
      description: 'Unified data intelligence layer',
      cx: 600,
      cy: 400,
      r: 45,
      color: layerColors.core,
      layer: 0,
      details: [
        'Multi-source monitoring',
        'Real-time data integration',
        'ML-powered analysis',
        '30+ languages processed'
      ]
    },
    
    // Layer 1 - Core Components (inner circle)
    {
      id: 'event-detection',
      name: 'EVENT DETECTION',
      description: 'AI-powered crisis identification',
      cx: 600,
      cy: 280,
      r: 38,
      color: layerColors.layer1.eventDetection,
      layer: 1,
      details: [
        'Multi-source monitoring',
        'Pattern recognition ML',
        'Smart alerting system',
        '85% accuracy rate',
        'Somalia drought: 6 weeks early'
      ]
    },
    {
      id: 'field-operations',
      name: 'FIELD OPERATIONS',
      description: 'Mobile-first command center',
      cx: 720,
      cy: 400,
      r: 38,
      color: layerColors.layer1.fieldOperations,
      layer: 1,
      details: [
        'Unified operational view',
        'Real-time resource tracking',
        'Team coordination maps',
        'Offline-first design',
        'Natural language queries'
      ]
    },
    {
      id: 'situation-analysis',
      name: 'SITUATION ANALYSIS',
      description: 'Intelligent report synthesis',
      cx: 600,
      cy: 520,
      r: 38,
      color: layerColors.layer1.situationAnalysis,
      layer: 1,
      details: [
        'Automated report synthesis',
        'Multi-language processing',
        'Trend identification',
        'Scenario planning',
        'Collaborative analysis',
        '6 hours → 15 minutes'
      ]
    },
    {
      id: 'cash-distribution',
      name: 'CASH DISTRIBUTION',
      description: 'Automated assistance pipeline',
      cx: 480,
      cy: 400,
      r: 38,
      color: layerColors.layer1.cashDistribution,
      layer: 1,
      details: [
        'Automated vulnerability assessment',
        'Real-time impact analysis',
        'Predictive vulnerability scoring',
        'Community validation',
        'Context-aware selection'
      ]
    },

    // Layer 2 - Supporting Systems (middle circle)
    {
      id: 'data-sources',
      name: 'DATA SOURCES',
      description: 'Multi-stream monitoring',
      cx: 500,
      cy: 230,
      r: 32,
      color: layerColors.layer2.dataSupport,
      layer: 2,
      details: [
        'Satellite imagery',
        'Weather data',
        'Social media feeds',
        'News monitoring',
        'Commodity prices',
        'Field reports'
      ]
    },
    {
      id: 'ml-models',
      name: 'ML MODELS',
      description: 'Pattern recognition',
      cx: 700,
      cy: 230,
      r: 32,
      color: layerColors.layer2.dataSupport,
      layer: 2,
      details: [
        '10 years crisis data training',
        'Anomaly detection',
        'False positive reduction',
        'Continuous learning',
        'Human feedback loop'
      ]
    },
    {
      id: 'coordination',
      name: 'COORDINATION',
      description: 'Multi-agency collaboration',
      cx: 800,
      cy: 340,
      r: 32,
      color: layerColors.layer2.operationalSupport,
      layer: 2,
      details: [
        'Partner activity mapping',
        'Resource deduplication',
        'Shared operational picture',
        'Communication protocols'
      ]
    },
    {
      id: 'assessment',
      name: 'ASSESSMENT',
      description: 'Impact evaluation',
      cx: 800,
      cy: 460,
      r: 32,
      color: layerColors.layer2.analysisSupport,
      layer: 2,
      details: [
        'Household composition analysis',
        'Asset loss evaluation',
        'Displacement tracking',
        'Protection risk assessment'
      ]
    },
    {
      id: 'reporting',
      name: 'REPORTING',
      description: 'Automated intelligence',
      cx: 700,
      cy: 570,
      r: 32,
      color: layerColors.layer2.analysisSupport,
      layer: 2,
      details: [
        'Structured situation reports',
        'Multi-source synthesis',
        'Historical pattern analysis',
        'Decision support insights'
      ]
    },
    {
      id: 'validation',
      name: 'VALIDATION',
      description: 'Community feedback',
      cx: 500,
      cy: 570,
      r: 32,
      color: layerColors.layer2.distributionSupport,
      layer: 2,
      details: [
        'Community input integration',
        'Ground truth verification',
        'Cultural context awareness',
        'Local knowledge incorporation'
      ]
    },
    {
      id: 'distribution',
      name: 'DISTRIBUTION',
      description: 'Assistance delivery',
      cx: 400,
      cy: 460,
      r: 32,
      color: layerColors.layer2.distributionSupport,
      layer: 2,
      details: [
        'Infrastructure availability',
        'Security condition analysis',
        'Population preference mapping',
        'Optimal method selection'
      ]
    },
    {
      id: 'monitoring',
      name: 'MONITORING',
      description: 'Continuous oversight',
      cx: 400,
      cy: 340,
      r: 32,
      color: layerColors.layer2.operationalSupport,
      layer: 2,
      details: [
        'Real-time system health',
        'Performance metrics',
        'Quality assurance',
        'Continuous improvement'
      ]
    },

    // Layer 3 - External Integrations (outer circle)
    {
      id: 'humanitarian-partners',
      name: 'HUMANITARIAN PARTNERS',
      description: 'Global response network',
      cx: 450,
      cy: 180,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'UN OCHA',
        'WFP',
        'UNHCR',
        'UNICEF',
        'WHO',
        'NGO network'
      ]
    },
    {
      id: 'government-systems',
      name: 'GOVERNMENT DATA',
      description: 'Official sources',
      cx: 600,
      cy: 150,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'National statistics',
        'Administrative boundaries',
        'Population registries',
        'Infrastructure data'
      ]
    },
    {
      id: 'commercial-data',
      name: 'COMMERCIAL DATA',
      description: 'Private sector feeds',
      cx: 750,
      cy: 180,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'Satellite providers',
        'Telecommunications data',
        'Financial systems',
        'Supply chain info'
      ]
    },
    {
      id: 'field-teams',
      name: 'FIELD TEAMS',
      description: 'Ground operations',
      cx: 880,
      cy: 300,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'Mobile workforce',
        'Local implementers',
        'Community volunteers',
        'Technical specialists'
      ]
    },
    {
      id: 'beneficiaries',
      name: 'BENEFICIARIES',
      description: 'Affected populations',
      cx: 880,
      cy: 500,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'Crisis-affected people',
        'Vulnerable households',
        'Displaced populations',
        'At-risk communities'
      ]
    },
    {
      id: 'financial-systems',
      name: 'FINANCIAL SYSTEMS',
      description: 'Payment infrastructure',
      cx: 750,
      cy: 620,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'Mobile money platforms',
        'Banking networks',
        'Digital wallets',
        'Cash transfer systems'
      ]
    },
    {
      id: 'research-institutions',
      name: 'RESEARCH INSTITUTIONS',
      description: 'Academic partnerships',
      cx: 600,
      cy: 650,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'Universities',
        'Think tanks',
        'Research centers',
        'Technical advisors'
      ]
    },
    {
      id: 'technology-providers',
      name: 'TECHNOLOGY PROVIDERS',
      description: 'Platform infrastructure',
      cx: 450,
      cy: 620,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'Cloud platforms',
        'AI/ML services',
        'Communication tools',
        'Data storage'
      ]
    },
    {
      id: 'communities',
      name: 'COMMUNITIES',
      description: 'Local knowledge networks',
      cx: 320,
      cy: 500,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'Community leaders',
        'Local organizations',
        'Traditional authorities',
        'Social networks'
      ]
    },
    {
      id: 'donors',
      name: 'DONORS',
      description: 'Funding partners',
      cx: 320,
      cy: 300,
      r: 28,
      color: layerColors.layer3,
      layer: 3,
      details: [
        'Government donors',
        'Private foundations',
        'Corporate partners',
        'Individual supporters'
      ]
    }
  ];

  // Generate connection lines between components
  const generateConnections = () => {
    const connections = [];
    
    // Core to Layer 1 connections
    const layer1Components = components.filter(c => c.layer === 1);
    layer1Components.forEach(comp => {
      connections.push({
        from: components[0], // data-mesh
        to: comp,
        strength: 0.8,
        type: 'primary'
      });
    });

    // Layer 1 to Layer 2 connections
    const connectionMap = {
      'event-detection': ['data-sources', 'ml-models'],
      'field-operations': ['coordination', 'monitoring'],
      'situation-analysis': ['reporting', 'assessment'],
      'cash-distribution': ['distribution', 'validation']
    };

    Object.entries(connectionMap).forEach(([parentId, childIds]) => {
      const parent = components.find(c => c.id === parentId);
      childIds.forEach(childId => {
        const child = components.find(c => c.id === childId);
        if (parent && child) {
          connections.push({
            from: parent,
            to: child,
            strength: 0.6,
            type: 'secondary'
          });
        }
      });
    });

    // Layer 2 to Layer 3 connections (selected key connections)
    const layer2ToLayer3 = {
      'data-sources': ['humanitarian-partners', 'government-systems', 'commercial-data'],
      'coordination': ['field-teams'],
      'assessment': ['beneficiaries'],
      'distribution': ['financial-systems'],
      'validation': ['communities'],
      'reporting': ['research-institutions'],
      'monitoring': ['technology-providers']
    };

    Object.entries(layer2ToLayer3).forEach(([parentId, childIds]) => {
      const parent = components.find(c => c.id === parentId);
      childIds.forEach(childId => {
        const child = components.find(c => c.id === childId);
        if (parent && child) {
          connections.push({
            from: parent,
            to: child,
            strength: 0.4,
            type: 'tertiary'
          });
        }
      });
    });

    return connections;
  };

  const connections = generateConnections();

  const handleComponentClick = (component: any) => {
    if (onComponentClick) {
      onComponentClick(component.id);
    }
    setHoveredComponent(component.id);
  };

  return (
    <div className={`relative ${className}`}>
      <svg className="w-full h-full" viewBox="0 0 1200 800" fill="none">
        <defs>
          <radialGradient id="coreGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={spectrumOrange} />
            <stop offset="100%" stopColor={spectrumAccent} />
          </radialGradient>
          <linearGradient id="layer1Gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={spectrumAccent} />
            <stop offset="100%" stopColor="#FF8A5C" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="shadow">
            <feDropShadow dx="2" dy="2" stdDeviation="2" floodOpacity="0.3"/>
          </filter>
        </defs>
        
        {/* Labeled Concentric Circles */}
        {/* Layer 1: AI Components Circle */}
        <motion.circle 
          cx="600" cy="400" r="120" 
          fill="none" 
          stroke={layerColors.circles.layer1} 
          strokeWidth="2" 
          opacity="0.5" 
          strokeDasharray="8,4"
          initial={{ pathLength: 0 }}
          animate={animated ? { pathLength: 1 } : {}}
          transition={{ duration: 2, delay: 0.5 }}
        />
        
        {/* Layer 2: Support Systems Circle */}
        <motion.circle 
          cx="600" cy="400" r="220" 
          fill="none" 
          stroke={layerColors.circles.layer2} 
          strokeWidth="2" 
          opacity="0.5" 
          strokeDasharray="6,3"
          initial={{ pathLength: 0 }}
          animate={animated ? { pathLength: 1 } : {}}
          transition={{ duration: 2, delay: 0.7 }}
        />

        {/* Layer 3: External Integrations Circle */}
        <motion.circle 
          cx="600" cy="400" r="320" 
          fill="none" 
          stroke={layerColors.circles.layer3} 
          strokeWidth="2" 
          opacity="0.5" 
          strokeDasharray="4,2"
          initial={{ pathLength: 0 }}
          animate={animated ? { pathLength: 1 } : {}}
          transition={{ duration: 2, delay: 0.9 }}
        />
        
        {/* Concentric Circle Labels - Moved to outer margins */}
        <g transform="translate(50, 200)">
          <motion.rect 
            x="0" y="0" width="140" height="90" 
            fill="white" 
            stroke={layerColors.circles.layer1} 
            strokeWidth="2" 
            opacity="0.95"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={animated ? { opacity: 0.95, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 1 }}
          />
          <motion.text 
            x="70" y="20" 
            textAnchor="middle"
            fontSize="11" 
            fill={layerColors.circles.layer1} 
            fontFamily="monospace" 
            fontWeight="bold"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.2 }}
          >
            LAYER 1
          </motion.text>
          <motion.text 
            x="70" y="35" 
            textAnchor="middle"
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.3 }}
          >
            AI COMPONENTS
          </motion.text>
          <motion.text 
            x="70" y="50" 
            textAnchor="middle"
            fontSize="7" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.4 }}
          >
            Event Detection
          </motion.text>
          <motion.text 
            x="70" y="62" 
            textAnchor="middle"
            fontSize="7" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.5 }}
          >
            Field Operations
          </motion.text>
          <motion.text 
            x="70" y="74" 
            textAnchor="middle"
            fontSize="7" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.6 }}
          >
            Analysis & Distribution
          </motion.text>
        </g>
        
        <g transform="translate(1010, 200)">
          <motion.rect 
            x="0" y="0" width="160" height="90" 
            fill="white" 
            stroke={layerColors.circles.layer2} 
            strokeWidth="2" 
            opacity="0.95"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={animated ? { opacity: 0.95, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 1.2 }}
          />
          <motion.text 
            x="80" y="20" 
            textAnchor="middle"
            fontSize="11" 
            fill={layerColors.circles.layer2} 
            fontFamily="monospace" 
            fontWeight="bold"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.4 }}
          >
            LAYER 2
          </motion.text>
          <motion.text 
            x="80" y="35" 
            textAnchor="middle"
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.5 }}
          >
            SUPPORT SYSTEMS
          </motion.text>
          <motion.text 
            x="80" y="50" 
            textAnchor="middle"
            fontSize="7" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.6 }}
          >
            Data Sources & ML Models
          </motion.text>
          <motion.text 
            x="80" y="62" 
            textAnchor="middle"
            fontSize="7" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.7 }}
          >
            Coordination & Assessment
          </motion.text>
          <motion.text 
            x="80" y="74" 
            textAnchor="middle"
            fontSize="7" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.8 }}
          >
            Reporting & Validation
          </motion.text>
        </g>
        
        <g transform="translate(50, 650)">
          <motion.rect 
            x="0" y="0" width="160" height="70" 
            fill="white" 
            stroke={layerColors.circles.layer3} 
            strokeWidth="2" 
            opacity="0.95"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={animated ? { opacity: 0.95, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 1.4 }}
          />
          <motion.text 
            x="80" y="20" 
            textAnchor="middle"
            fontSize="11" 
            fill={layerColors.circles.layer3} 
            fontFamily="monospace" 
            fontWeight="bold"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.6 }}
          >
            LAYER 3
          </motion.text>
          <motion.text 
            x="80" y="35" 
            textAnchor="middle"
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.7 }}
          >
            EXTERNAL INTEGRATIONS
          </motion.text>
          <motion.text 
            x="80" y="50" 
            textAnchor="middle"
            fontSize="7" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.8 }}
          >
            Partners, Government, Commercial
          </motion.text>
          <motion.text 
            x="80" y="62" 
            textAnchor="middle"
            fontSize="7" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 1.9 }}
          >
            Communities & Technology Providers
          </motion.text>
        </g>

        {/* Connection lines with improved relationship visualization */}
        {connections.map((connection, i) => {
          const isHighlighted = hoveredComponent === connection.from.id || hoveredComponent === connection.to.id;
          const connectionColor = connection.type === 'primary' ? layerColors.connections.primary : 
                                connection.type === 'secondary' ? layerColors.connections.secondary : 
                                layerColors.connections.tertiary;
          
          return (
            <g key={i}>
              {/* Main connection line */}
              <motion.line
                x1={connection.from.cx}
                y1={connection.from.cy}
                x2={connection.to.cx}
                y2={connection.to.cy}
                stroke={connectionColor}
                strokeWidth={isHighlighted ? connection.strength * 4 : connection.strength * 2.5}
                opacity={isHighlighted ? 0.9 : connection.strength * 0.7}
                strokeDasharray={connection.type === 'tertiary' ? "4,2" : 
                               connection.type === 'secondary' ? "6,3" : "none"}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={animated ? { 
                  pathLength: 1, 
                  opacity: isHighlighted ? 0.9 : connection.strength * 0.7 
                } : {}}
                transition={{ 
                  duration: 2, 
                  delay: i * 0.05,
                  ease: "easeInOut"
                }}
              />
              
              {/* Connection direction indicator for primary connections */}
              {connection.type === 'primary' && (
                <motion.circle
                  cx={(connection.from.cx + connection.to.cx) / 2}
                  cy={(connection.from.cy + connection.to.cy) / 2}
                  r={3}
                  fill={connectionColor}
                  opacity={isHighlighted ? 1 : 0.8}
                  initial={{ scale: 0 }}
                  animate={animated ? { 
                    scale: [0, 1.2, 1],
                    opacity: isHighlighted ? 1 : 0.8 
                  } : {}}
                  transition={{ 
                    duration: 1, 
                    delay: i * 0.05 + 1,
                    ease: "easeOut"
                  }}
                />
              )}
            </g>
          );
        })}

        {/* Enhanced Data flow visualization */}
        {animated && (
          <>
            {/* Core to Layer 1 data flow */}
            {components.filter(c => c.layer === 1).map((comp, i) => (
              <motion.circle
                key={`flow-core-${i}`}
                r={3}
                fill={layerColors.connections.primary}
                initial={{ 
                  cx: 400, 
                  cy: 300, 
                  opacity: 1 
                }}
                animate={{ 
                  cx: [400, comp.cx], 
                  cy: [300, comp.cy],
                  opacity: [1, 0.8, 0],
                  r: [3, 2, 1]
                }}
                transition={{ 
                  duration: 2.5, 
                  repeat: Infinity, 
                  delay: i * 0.6,
                  ease: "easeOut"
                }}
              />
            ))}
            
            {/* Layer 1 to Layer 2 data flow */}
            {components.filter(c => c.layer === 2).slice(0, 4).map((comp, i) => {
              const layer1Components = components.filter(c => c.layer === 1);
              const sourceComponent = layer1Components[i % layer1Components.length];
              return (
                <motion.circle
                  key={`flow-l1-l2-${i}`}
                  r={2}
                  fill={layerColors.connections.secondary}
                  initial={{ 
                    cx: sourceComponent.cx, 
                    cy: sourceComponent.cy, 
                    opacity: 0.8 
                  }}
                  animate={{ 
                    cx: [sourceComponent.cx, comp.cx], 
                    cy: [sourceComponent.cy, comp.cy],
                    opacity: [0.8, 0.6, 0],
                    r: [2, 1.5, 0.5]
                  }}
                  transition={{ 
                    duration: 2, 
                    repeat: Infinity, 
                    delay: i * 0.8 + 1,
                    ease: "easeOut"
                  }}
                />
              );
            })}
            
            {/* Layer 2 to Layer 3 data flow */}
            {components.filter(c => c.layer === 3).slice(0, 3).map((comp, i) => {
              const layer2Components = components.filter(c => c.layer === 2);
              const sourceComponent = layer2Components[i * 2];
              return sourceComponent ? (
                <motion.circle
                  key={`flow-l2-l3-${i}`}
                  r={1.5}
                  fill={layerColors.connections.tertiary}
                  initial={{ 
                    cx: sourceComponent.cx, 
                    cy: sourceComponent.cy, 
                    opacity: 0.6 
                  }}
                  animate={{ 
                    cx: [sourceComponent.cx, comp.cx], 
                    cy: [sourceComponent.cy, comp.cy],
                    opacity: [0.6, 0.4, 0],
                    r: [1.5, 1, 0.5]
                  }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity, 
                    delay: i * 1 + 2,
                    ease: "easeOut"
                  }}
                />
              ) : null;
            })}
          </>
        )}

        {/* Component nodes */}
        {components.map((component, i) => {
          const isHovered = hoveredComponent === component.id;
          const isCore = component.layer === 0;
          const isLayer1 = component.layer === 1;
          
          return (
            <g key={component.id}>
              {/* Component circle */}
              <motion.circle
                cx={component.cx}
                cy={component.cy}
                r={isHovered ? component.r * 1.1 : component.r}
                fill={isCore ? "url(#coreGradient)" : 
                      isLayer1 ? "url(#layer1Gradient)" : 
                      component.color}
                stroke={isHovered ? spectrumOrange : 
                        isCore ? "white" : 
                        spectrumLight}
                strokeWidth={isHovered ? "4" : isCore ? "3" : "2"}
                filter={isCore ? "url(#glow)" : isHovered ? "url(#shadow)" : "none"}
                initial={{ scale: 0, opacity: 0 }}
                animate={animated ? { 
                  scale: 1, 
                  opacity: 1,
                  ...(isCore ? { rotate: [0, 360] } : {})
                } : {}}
                transition={{ 
                  duration: isCore ? 2 : 1, 
                  delay: i * 0.1,
                  ...(isCore ? { rotate: { duration: 20, repeat: Infinity, ease: "linear" } } : {})
                }}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredComponent(component.id)}
                onMouseLeave={() => setHoveredComponent(null)}
                onClick={() => handleComponentClick(component)}
              />
              
              {/* Component label */}
              <motion.text 
                x={component.cx} 
                y={component.cy - (component.r > 35 ? 5 : 0)} 
                textAnchor="middle" 
                fontSize={component.r > 35 ? "9" : "7"}
                fill={isCore || isLayer1 ? "white" : spectrumDark} 
                fontFamily="monospace"
                fontWeight={isCore ? "bold" : "normal"}
                initial={{ opacity: 0 }}
                animate={animated ? { opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 + 0.5 }}
                style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoveredComponent(component.id)}
                onMouseLeave={() => setHoveredComponent(null)}
                onClick={() => handleComponentClick(component)}
              >
                {component.name}
              </motion.text>
              
              {/* Component description for larger components */}
              {component.r > 32 && (
                <motion.text 
                  x={component.cx} 
                  y={component.cy + (component.r > 35 ? 8 : 5)} 
                  textAnchor="middle" 
                  fontSize="6"
                  fill={isCore || isLayer1 ? "white" : spectrumMuted} 
                  fontFamily="monospace"
                  initial={{ opacity: 0 }}
                  animate={animated ? { opacity: 0.8 } : {}}
                  transition={{ duration: 0.5, delay: i * 0.1 + 0.7 }}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredComponent(component.id)}
                  onMouseLeave={() => setHoveredComponent(null)}
                  onClick={() => handleComponentClick(component)}
                >
                  {component.description}
                </motion.text>
              )}

              {/* Activity pulse for core component */}
              {isCore && (
                <motion.circle
                  cx={component.cx}
                  cy={component.cy}
                  r={component.r}
                  fill="none"
                  stroke={spectrumOrange}
                  strokeWidth="2"
                  opacity="0.4"
                  animate={animated ? { 
                    r: [component.r, component.r + 15, component.r],
                    opacity: [0.4, 0.8, 0.4]
                  } : {}}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "easeInOut"
                  }}
                />
              )}
            </g>
          );
        })}

        {/* System Architecture Legend - moved to far left */}
        <g transform="translate(20, 350)">
          <motion.rect 
            x="0" y="0" width="200" height="220" 
            fill="white" 
            stroke={spectrumOrange} 
            strokeWidth="2" 
            opacity="0.95"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={animated ? { opacity: 0.95, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 2 }}
          />
          
          <motion.text 
            x="100" y="20" 
            textAnchor="middle"
            fontSize="12" 
            fill={spectrumOrange} 
            fontFamily="monospace" 
            fontWeight="bold"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 2.2 }}
          >
            SYSTEM ARCHITECTURE
          </motion.text>
          <motion.text 
            x="100" y="35" 
            textAnchor="middle"
            fontSize="8" 
            fill={spectrumMuted} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 2.3 }}
          >
            AI-Powered Humanitarian Platform
          </motion.text>
          
          {/* Core indicator */}
          <g transform="translate(20, 50)">
            <motion.circle 
              cx="8" cy="8" r="8" 
              fill="url(#coreGradient)" 
              stroke="white" 
              strokeWidth="2"
              initial={{ scale: 0 }}
              animate={animated ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 2.4 }}
            />
            <motion.text 
              x="25" y="12" 
              fontSize="9" 
              fill={spectrumDark} 
              fontFamily="monospace" 
              fontWeight="bold"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 2.5 }}
            >
              CORE: Data Mesh Intelligence Layer
            </motion.text>
          </g>
          
          {/* Layer 1 components with colors */}
          <g transform="translate(20, 80)">
            <motion.text 
              x="0" y="0" 
              fontSize="9" 
              fill={spectrumDark} 
              fontFamily="monospace" 
              fontWeight="bold"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 2.6 }}
            >
              AI COMPONENTS (Layer 1)
            </motion.text>
            
            <motion.circle 
              cx="8" cy="20" r="5" 
              fill={layerColors.layer1.eventDetection} 
              stroke={spectrumLight} 
              strokeWidth="1"
              initial={{ scale: 0 }}
              animate={animated ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 2.7 }}
            />
            <motion.text 
              x="20" y="24" 
              fontSize="7" 
              fill={spectrumDark} 
              fontFamily="monospace"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 2.8 }}
            >
              Event Detection (Blue)
            </motion.text>
            
            <motion.circle 
              cx="8" cy="37" r="5" 
              fill={layerColors.layer1.fieldOperations} 
              stroke={spectrumLight} 
              strokeWidth="1"
              initial={{ scale: 0 }}
              animate={animated ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 2.9 }}
            />
            <motion.text 
              x="20" y="41" 
              fontSize="7" 
              fill={spectrumDark} 
              fontFamily="monospace"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.0 }}
            >
              Field Operations (Green)
            </motion.text>
            
            <motion.circle 
              cx="8" cy="54" r="5" 
              fill={layerColors.layer1.situationAnalysis} 
              stroke={spectrumLight} 
              strokeWidth="1"
              initial={{ scale: 0 }}
              animate={animated ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.1 }}
            />
            <motion.text 
              x="20" y="58" 
              fontSize="7" 
              fill={spectrumDark} 
              fontFamily="monospace"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.2 }}
            >
              Situation Analysis (Red)
            </motion.text>
            
            <motion.circle 
              cx="8" cy="71" r="5" 
              fill={layerColors.layer1.cashDistribution} 
              stroke={spectrumLight} 
              strokeWidth="1"
              initial={{ scale: 0 }}
              animate={animated ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.3 }}
            />
            <motion.text 
              x="20" y="75" 
              fontSize="7" 
              fill={spectrumDark} 
              fontFamily="monospace"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.4 }}
            >
              Cash Distribution (Purple)
            </motion.text>
          </g>
          
          {/* Data Flow Legend */}
          <g transform="translate(20, 175)">
            <motion.text 
              x="0" y="0" 
              fontSize="9" 
              fill={spectrumDark} 
              fontFamily="monospace" 
              fontWeight="bold"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.5 }}
            >
              DATA FLOW PATTERNS
            </motion.text>
            
            <motion.circle 
              cx="8" cy="20" r="3" 
              fill={layerColors.connections.primary}
              initial={{ scale: 0 }}
              animate={animated ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.6 }}
            />
            <motion.text 
              x="20" y="23" 
              fontSize="7" 
              fill={spectrumDark} 
              fontFamily="monospace"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.7 }}
            >
              Core → AI Components
            </motion.text>
            
            <motion.circle 
              cx="8" cy="37" r="2" 
              fill={layerColors.connections.secondary}
              initial={{ scale: 0 }}
              animate={animated ? { scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.8 }}
            />
            <motion.text 
              x="20" y="40" 
              fontSize="7" 
              fill={spectrumDark} 
              fontFamily="monospace"
              initial={{ opacity: 0 }}
              animate={animated ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 3.9 }}
            >
              AI → Support Systems
            </motion.text>
          </g>
        </g>

        {/* Performance indicators positioned in far right space */}
        <g transform="translate(1010, 350)">
          <motion.rect 
            x="0" y="0" width="170" height="130" 
            fill="white" 
            stroke={layerColors.layer1.situationAnalysis} 
            strokeWidth="2" 
            opacity="0.95"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={animated ? { opacity: 0.95, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: 2.5 }}
          />
          
          <motion.text 
            x="85" y="20" 
            textAnchor="middle"
            fontSize="12" 
            fill={layerColors.layer1.situationAnalysis} 
            fontFamily="monospace" 
            fontWeight="bold"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 2.7 }}
          >
            KEY METRICS
          </motion.text>
          
          <motion.text 
            x="10" y="40" 
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 2.8 }}
          >
            • 6 weeks early detection
          </motion.text>
          
          <motion.text 
            x="10" y="55" 
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 2.9 }}
          >
            • 85% accuracy rate
          </motion.text>
          
          <motion.text 
            x="10" y="70" 
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 3.0 }}
          >
            • 6hrs → 15min reports
          </motion.text>
          
          <motion.text 
            x="10" y="85" 
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 3.1 }}
          >
            • 30+ languages processed
          </motion.text>
          
          <motion.text 
            x="10" y="100" 
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 3.2 }}
          >
            • Real-time coordination
          </motion.text>
          
          <motion.text 
            x="10" y="115" 
            fontSize="9" 
            fill={spectrumDark} 
            fontFamily="monospace"
            initial={{ opacity: 0 }}
            animate={animated ? { opacity: 1 } : {}}
            transition={{ duration: 0.5, delay: 3.3 }}
          >
            • Offline-first operation
          </motion.text>
        </g>
      </svg>

      {/* Component details popup */}
      {hoveredComponent && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-4 left-4 bg-white border-2 border-gray-300 p-4 max-w-xs"
          style={{ zIndex: 10 }}
        >
          {(() => {
            const component = components.find(c => c.id === hoveredComponent);
            return component ? (
              <div>
                <h4 className="font-bold text-sm mb-2" style={{color: component.color}}>
                  {component.name}
                </h4>
                <p className="text-xs text-gray-600 mb-3">{component.description}</p>
                <ul className="text-xs space-y-1">
                  {component.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <div className="w-1 h-1 bg-gray-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null;
          })()}
        </motion.div>
      )}
    </div>
  );
};

export default CLEARArchitectureDiagram;